import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { match } from 'path-to-regexp';
import moment from 'moment';
import jsonBig from 'json-bigint';

import { DynamicPathList, URLTimeZoneConfig } from './timezoneConfig';
import { UserIntlStorage } from './intl';
import { getClientReportInfo } from './monitor';
import { getCodeInfoFromDomain } from './index';

// 动态接口规则
const DynamicPathRuleList = DynamicPathList.map((item) => {
  return {
    path: item,
    match: match(item),
  };
});

// 匹配规则
const matchTimeZoneConfig = (request: AxiosRequestConfig) => {
  const { url, method } = request;
  // 接口配置
  let matchItem: typeof URLTimeZoneConfig['/approval/detail'] = URLTimeZoneConfig[url];

  if (!matchItem) {
    // 动态接口
    const dynamicItem = DynamicPathRuleList.find((item) => item.match(url));
    matchItem = URLTimeZoneConfig[dynamicItem?.path];
  }

  if (matchItem && matchItem.method === method.toUpperCase()) {
    return matchItem;
  }

  return null;
};

const parseToString = Object.prototype.toString;
const isObject = (val) => parseToString.call(val) === '[object Object]';
const isArray = (val) => parseToString.call(val) === '[object Array]';

// 转换指定参数时区
let timezoneParamList = [];
const BaseTimeZoneOffset = 480; // 基础(北京)时间偏移量
const parseDataTimeZone = (data, keyList: string[], offset) => {
  if (isObject(data)) {
    const result = {};
    // eslint-disable-next-line guard-for-in
    for (const childKey in data) {
      if (keyList.includes(childKey)) {
        const originValue = data[childKey];
        result[childKey] = originValue;
        // 不会是array或object
        if (typeof originValue === 'number') {
          const offsetValue = originValue + offset;
          timezoneParamList.push({ name: childKey, originValue, offsetValue });
          result[childKey] = offsetValue;
        }
      } else {
        result[childKey] = parseDataTimeZone(data[childKey], keyList, offset);
      }
    }
    return result;
  }

  if (isArray(data)) {
    return data.map((child) => parseDataTimeZone(child, keyList, offset));
  }

  return data;
};

// 请求入参时区
export const parseRequestTimeZone = (request: AxiosRequestConfig) => {
  const rule = matchTimeZoneConfig(request);
  let result = request;
  timezoneParamList = [];
  if (rule) {
    const { requestKeys, requestInterceptor } = rule;
    // 将当前时区的时间 转换为东八(北京)时间
    const offset = -(BaseTimeZoneOffset - moment().utcOffset()) * 60 * 1000;
    if (requestKeys && requestKeys.length) {
      result = {
        ...request,
        params: parseDataTimeZone(request.params, requestKeys, offset),
        data: parseDataTimeZone(request.data, requestKeys, offset),
      };
    }
    if (typeof requestInterceptor === 'function') {
      result = requestInterceptor(request);
    }
  }
  if (timezoneParamList.length) {
    console.groupCollapsed('入参偏移', request.url);
    console.table(timezoneParamList, ['name', 'originValue', 'offsetValue']);
    console.groupEnd();
  }
  return result;
};

// 请求出参时区
export const parseResponseTimeZone = (response: AxiosResponse) => {
  const rule = matchTimeZoneConfig(response.config);
  let result = response.data;
  timezoneParamList = [];
  if (rule) {
    const { responseKeys, responseInterceptor } = rule;
    // 将东八(北京)时间 转换为当前时区的时间
    const offset = (BaseTimeZoneOffset - moment().utcOffset()) * 60 * 1000;
    if (responseKeys && responseKeys.length) {
      result = {
        ...response.data,
        data: parseDataTimeZone(response.data.data, responseKeys, offset),
      };
    }
    if (typeof responseInterceptor === 'function') {
      result = responseInterceptor(response.data);
    }
  }
  if (timezoneParamList.length) {
    console.groupCollapsed('出参偏移', response.config.url);
    console.table(timezoneParamList, ['name', 'originValue', 'offsetValue']);
    console.groupEnd();
  }
  return result;
};

const chainCode = getCodeInfoFromDomain().code;

// 全局请求头
export const parseGlobalRequestHeader = (config: AxiosRequestConfig) => {
  const updateHeader = config.headers || {};
  const intlInfo = UserIntlStorage.getLocalConfig();
  const systemInfo = getClientReportInfo();

  const headerList = [
    { name: 'i18n-chain', value: chainCode },
    { name: 'i18n-language', value: intlInfo.Language },
    { name: 'i18n-zone-time', value: intlInfo.TimeZoneCode },
    { name: 'version', value: systemInfo.version },
  ];

  headerList.forEach((item) => {
    if (item.value) {
      updateHeader[item.name] = item.value;
    }
  });

  return {
    ...config,
    headers: updateHeader,
  };
};

// 处理后端长整型数据精度丢失
export const parseLongDataTransformResponse = (data: any) => {
  try {
    const json = jsonBig({ storeAsString: true }); // 只针对长整型处理成字符串格式
    return json.parse(data);
  } catch (error) {
    return data;
  }
};

// header：traceparent 配合后端进行漏洞修复(2024.06.07薛利明提出)    key:traceparent  value: 00-{traceId}-{spanid}-01
const SHARED_CHAR_CODES_ARRAY = Array(32);
const SPAN_ID_BYTES = 8;
const TRACE_ID_BYTES = 16;
function getIdGenerator(bytes: number): () => string {
  return function generateId() {
    for (let i = 0; i < bytes * 2; i++) {
      SHARED_CHAR_CODES_ARRAY[i] = Math.floor(Math.random() * 16) + 48;
      // valid hex characters in the range 48-57 and 97-102
      if (SHARED_CHAR_CODES_ARRAY[i] >= 58) {
        SHARED_CHAR_CODES_ARRAY[i] += 39;
      }
    }
    return String.fromCharCode.apply(null, SHARED_CHAR_CODES_ARRAY.slice(0, bytes * 2));
  };
}
export const generateTraceparent = () => {
  const traceId = getIdGenerator(TRACE_ID_BYTES);
  const spanid = getIdGenerator(SPAN_ID_BYTES);
  return `00-${traceId()}-${spanid()}-01`;
};
